import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef, inject } from '@angular/core';
import { PermissionService } from '@core/services/permission.service';
import { Subscription } from 'rxjs';

/**
 * Structural directive to show/hide elements based on permissions
 *
 * Usage examples:
 *
 * // Single permission
 * <div *hasPermission="'campaign_create'">Create Campaign Button</div>
 *
 * // Multiple permissions (ANY by default)
 * <div *hasPermission="['campaign_create', 'campaign_update']">Campaign Actions</div>
 *
 * // Multiple permissions (ALL required)
 * <div *hasPermission="['admin_access', 'super_admin']; requireAll: true">Super Admin Panel</div>
 *
 * // Role-based
 * <div *hasPermission="null; roles: 'admin'">Admin Content</div>
 * <div *hasPermission="null; roles: ['admin', 'campaign']">Admin or Campaign Manager Content</div>
 *
 * // Combined permissions and roles
 * <div *hasPermission="'special_access'; roles: ['admin', 'campaign']; requireAll: true">Special Admin Content</div>
 *
 * // Positive and negative permissions (has X but not Y)
 * <div *hasPermission="'campaign_create'; not: 'campaign_update'">Create without Update</div>
 * <div *hasPermission="['campaign_create', 'campaign_delete']; not: ['campaign_update', 'campaign_publish']">Complex Case</div>
 *
 * // Hide instead of show (show when permission is NOT present)
 * <div *hasPermission="'admin_access'; hide: true">Content for non-admins</div>
 *
 * // Permission groups (OR of AND groups)
 * // User needs: (campaign_create AND campaign_update) OR (campaign_delete AND campaign_publish AND campaign_view)
 * <div *hasPermissionGroups="[['campaign_create', 'campaign_update'], ['campaign_delete', 'campaign_publish', 'campaign_view']]">Grouped Permissions</div>
 */
@Directive({
  selector: '[hasPermission], [hasPermissionGroups]',
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly permissionService = inject(PermissionService);

  private subscription: Subscription = new Subscription();
  private currentPermissions: string | string[] | null = null;
  private currentNotPermissions: string | string[] | null = null;
  private currentRoles: string | string[] | null = null;
  private currentPermissionGroups: string[][] | null = null;
  private requireAll = false;
  private requireAllRoles = false;
  private hideWhenHasPermission = false;

  @Input() public set hasPermission(permissions: string | string[] | null) {
    this.currentPermissions = permissions;
    this.updateView();
  }

  @Input() public set hasPermissionNot(permissions: string | string[] | null) {
    this.currentNotPermissions = permissions;
    this.updateView();
  }

  @Input() public set hasPermissionRequireAll(requireAll: boolean) {
    this.requireAll = requireAll;
    this.updateView();
  }

  @Input() public set hasPermissionRoles(roles: string | string[] | null) {
    this.currentRoles = roles;
    this.updateView();
  }

  @Input() public set hasPermissionRequireAllRoles(requireAllRoles: boolean) {
    this.requireAllRoles = requireAllRoles;
    this.updateView();
  }

  @Input() public set hasPermissionHide(hide: boolean) {
    this.hideWhenHasPermission = hide;
    this.updateView();
  }

  @Input() public set hasPermissionGroups(groups: string[][] | null) {
    this.currentPermissionGroups = groups;
    this.updateView();
  }

  public ngOnInit(): void {
    this.updateView();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private updateView(): void {
    this.subscription.unsubscribe();
    this.subscription = new Subscription();

    if (
      !this.currentPermissions &&
      !this.currentRoles &&
      !this.currentNotPermissions &&
      !this.currentPermissionGroups
    ) {
      this.renderElement(!this.hideWhenHasPermission);

      return;
    }

    const permissionState = {
      hasPermission: true,
      hasPermissionGroup: true,
      hasRole: true,
      passesNegativePermissionConstraint: true,
    };

    this.checkPermissions(permissionState);
    this.checkNegativePermissions(permissionState);
    this.checkPermissionGroups(permissionState);
    this.checkRoles(permissionState);
  }

  private checkPermissions(permissionState: {
    hasPermission: boolean;
    hasPermissionGroup: boolean;
    hasRole: boolean;
    passesNegativePermissionConstraint: boolean;
  }): void {
    if (!this.currentPermissions) {
      return;
    }

    const permissionArray = Array.isArray(this.currentPermissions)
      ? this.currentPermissions
      : [this.currentPermissions];

    const permissionCheck$ = this.requireAll
      ? this.permissionService.hasAllPermissions$(permissionArray)
      : this.permissionService.hasAnyPermission$(permissionArray);

    this.subscription.add(
      permissionCheck$.subscribe((result: boolean) => {
        permissionState.hasPermission = result;
        this.evaluateAndRender(
          permissionState.hasPermission,
          permissionState.hasRole,
          permissionState.passesNegativePermissionConstraint,
          permissionState.hasPermissionGroup,
        );
      }),
    );
  }

  private checkNegativePermissions(permissionState: {
    hasPermission: boolean;
    hasPermissionGroup: boolean;
    hasRole: boolean;
    passesNegativePermissionConstraint: boolean;
  }): void {
    if (!this.currentNotPermissions) {
      return;
    }

    const notPermissionArray = Array.isArray(this.currentNotPermissions)
      ? this.currentNotPermissions
      : [this.currentNotPermissions];

    // For negative permissions, we pass if the user does NOT have ANY of the provided permissions
    this.subscription.add(
      this.permissionService.hasAnyPermission$(notPermissionArray).subscribe((result: boolean) => {
        permissionState.passesNegativePermissionConstraint = !result;
        this.evaluateAndRender(
          permissionState.hasPermission,
          permissionState.hasRole,
          permissionState.passesNegativePermissionConstraint,
          permissionState.hasPermissionGroup,
        );
      }),
    );
  }

  private checkPermissionGroups(permissionState: {
    hasPermission: boolean;
    hasPermissionGroup: boolean;
    hasRole: boolean;
    passesNegativePermissionConstraint: boolean;
  }): void {
    if (!this.currentPermissionGroups) {
      return;
    }

    // Check if user has at least one complete group (OR of AND groups)
    this.subscription.add(
      this.permissionService.currentUserPermissions$.subscribe((userPermissions: string[]) => {
        // For each group, check if user has ALL permissions in that group
        // Then check if at least one group passes (OR)
        permissionState.hasPermissionGroup = this.currentPermissionGroups!.some((group: string[]) =>
          group.every((permission: string) => userPermissions.includes(permission)),
        );
        this.evaluateAndRender(
          permissionState.hasPermission,
          permissionState.hasRole,
          permissionState.passesNegativePermissionConstraint,
          permissionState.hasPermissionGroup,
        );
      }),
    );
  }

  private checkRoles(permissionState: {
    hasPermission: boolean;
    hasPermissionGroup: boolean;
    hasRole: boolean;
    passesNegativePermissionConstraint: boolean;
  }): void {
    if (this.currentRoles) {
      const roleArray = Array.isArray(this.currentRoles) ? this.currentRoles : [this.currentRoles];

      if (this.requireAllRoles) {
        this.subscription.add(
          this.permissionService.getCurrentUserRoles$().subscribe((userRoles: string[]) => {
            permissionState.hasRole = roleArray.every((role) => userRoles.includes(role));
            this.evaluateAndRender(
              permissionState.hasPermission,
              permissionState.hasRole,
              permissionState.passesNegativePermissionConstraint,
              permissionState.hasPermissionGroup,
            );
          }),
        );
      } else {
        this.subscription.add(
          this.permissionService.hasAnyRole$(roleArray).subscribe((result: boolean) => {
            permissionState.hasRole = result;
            this.evaluateAndRender(
              permissionState.hasPermission,
              permissionState.hasRole,
              permissionState.passesNegativePermissionConstraint,
              permissionState.hasPermissionGroup,
            );
          }),
        );
      }
    } else if (!this.currentPermissionGroups && !this.currentPermissions && !this.currentNotPermissions) {
      this.evaluateAndRender(
        permissionState.hasPermission,
        permissionState.hasRole,
        permissionState.passesNegativePermissionConstraint,
        permissionState.hasPermissionGroup,
      );
    }
  }

  private evaluateAndRender(
    hasPermission: boolean,
    hasRole: boolean,
    passesNegativePermissionConstraint: boolean,
    hasPermissionGroup: boolean,
  ): void {
    // If permission groups are specified, use them; otherwise use regular permissions
    const permissionCheck = this.currentPermissionGroups ? hasPermissionGroup : hasPermission;
    const hasAccess: boolean = permissionCheck && hasRole && passesNegativePermissionConstraint;
    const shouldShow = this.hideWhenHasPermission ? !hasAccess : hasAccess;
    this.renderElement(shouldShow);
  }

  private renderElement(shouldShow: boolean): void {
    if (shouldShow) {
      if (this.viewContainer.length === 0) {
        this.viewContainer.createEmbeddedView(this.templateRef);
      }
    } else {
      this.viewContainer.clear();
    }
  }
}
